"use client";

import * as React from "react";

import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@hipotecar/ui/lib/utils";
import { Button } from "@hipotecar/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
	date?: Date;
	setDate: (date?: Date) => void;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
	minDate?: Date;
	maxDate?: Date;
	onOpenChange?: (open: boolean) => void;
}

export function DatePicker({
	date,
	setDate,
	placeholder,
	className,
	disabled,
	minDate,
	maxDate,
	onOpenChange,
}: DatePickerProps) {
	const { i18n, t } = useTranslation();
	const locale = i18n.language.startsWith("es") ? es : enUS;
	const translatedPlaceholder =
		placeholder || t("common:date_picker.placeholder");

	const [open, setOpen] = React.useState(false);

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		onOpenChange?.(newOpen);
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange} modal={true}>
			<PopoverTrigger asChild>
				<Button
					variant={"outline"}
					className={cn(
						"h-11 w-full justify-start bg-transparent text-left font-normal text-gray-900 hover:bg-transparent dark:bg-gray-900 dark:text-white/90 dark:hover:bg-gray-900",
						!date && "text-muted-foreground",
						className,
					)}
					disabled={disabled}
				>
					<CalendarIcon className="mr-2 size-4" />
					{date ? (
						format(date, "PPP", { locale })
					) : (
						<span>{translatedPlaceholder}</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					mode="single"
					selected={date}
					onSelect={(date, _, modifiers) => {
						// Keep selection in sync while navigating months/years/arrows
						// without closing the picker.
						if (modifiers?.viewChange) {
							setDate(date);
							return;
						}
						setDate(date);
						handleOpenChange(false);
					}}
					defaultMonth={date}
					initialFocus
					locale={locale}
					fromDate={minDate}
					toDate={maxDate}
					disabled={[
						...(minDate ? [{ before: minDate }] : []),
						...(maxDate ? [{ after: maxDate }] : []),
					]}
				/>
			</PopoverContent>
		</Popover>
	);
}
